import {
  ErrorModal,
  HelmetDexteel,
  useUTLSettingsContext,
} from "@dexteel/mesf-core";
import { Paper, Box } from "@mui/material";
import { get } from "lodash-es";
import { useEffect, useRef, useState } from "react";
import { NotificationSnackBar } from "../../controls/snackbars/notification-snackbar";
import {
  ClearLocationModalWrapper,
  EditLocationModalWrapper,
  InfoLocationModalWrapper,
  LoadingAndEmptyStates,
  LocationSelectionModalWrapper,
  LoginModalWrapper,
  MoveBilletsModalWrapper,
  NavigationControls,
  SearchModalWrapper,
  TopMenu,
} from "./components";
import {
  RegularLocationsSection,
  SpecialLocationsSection,
} from "./components/core/Sections";
import { MODAL_TYPES, TOTAL_PAGES } from "./constants";
import {
  useAuthenticationControls,
  useAuthenticationFlow,
  useBilletYardData,
  useCranes,
  useLocationInteractions,
  useModalManagement,
  usePagination,
  useSearchControls,
} from "./hooks";

export const BilletYardInventoryPage = () => {
  // Timer configuration from settings
  const { state } = useUTLSettingsContext();
  const refreshInterval = parseInt(
    get(state.settings, "BYI_APP_REFRESH_TIME", 5),
  );

  // Search notification message state
  const [searchNotificationMessage, setSearchNotificationMessage] = useState<
    string | null
  >(null);
  const [searchNotificationSeverity, setSearchNotificationSeverity] = useState<
    "success" | "warning"
  >("success");

  // Search criteria state - persistent across modal open/close
  const [searchCriteria, setSearchCriteria] = useState({
    locations: [] as string[],
    heats: [] as string[],
    sections: [] as string[],
    lengths: [] as string[],
    grades: [] as string[],
    qualities: [] as string[],
    destinations: [] as string[],
  });

  const [selectedLocationIdForEdition, setSelectedLocationIdForEdition] =
    useState<number | null>(null);

  const {
    currentPage: regularLocationPage,
    handlePageChange: handleRegularLocationPageChange,
  } = usePagination({ totalPages: TOTAL_PAGES });

  // Create refs for modal handlers to solve circular dependencies
  const modalCloseRef = useRef<(() => void) | null>(null);
  const openCraneSelectionModalRef = useRef<(() => void) | null>(null);
  const openLocationPickerModalRef = useRef<(() => void) | null>(null);

  // Data management with integrated search and processing
  const dataManager = useBilletYardData({
    regularLocationPage,
    searchCriteria,
    onPageChange: handleRegularLocationPageChange,
    onModalClose: () => modalCloseRef.current?.(),
  });

  // Clear location edition selection
  const clearLocationEdition = () => {
    setSelectedLocationIdForEdition(null);
  };

  // Crane selection management (must be defined before useAuthenticationFlow)
  const { selectCrane, selectedCraneId, clearSelection } = useCranes();

  // Authentication and authorization management with memory leak prevention
  const authFlow = useAuthenticationFlow({
    onOpenCraneSelectionModal: () => openCraneSelectionModalRef.current?.(),
    onOpenLocationPickerModal: () => openLocationPickerModalRef.current?.(),
    onClearCraneSelection: clearSelection,
  });

  // Location interactions management with memory leak prevention
  const {
    sourceLocationId,
    destinationLocationId,
    selectedLocationIdForClear,
    handleLocationClick: baseHandleLocationClick,
    handleLocationPick: baseHandleLocationPick,
    handleLocationHoldTap: baseHandleLocationHoldTap,
    clearAllSelections,
    clearLocationForClear,
  } = useLocationInteractions({
    canOpenMoveBilletsModal: authFlow.canOpenMoveBilletsModal,
  });

  // Modal management with memory leak prevention
  const {
    currentModal,
    isModalOpen,
    openSearchModal,
    openLocationPickerModal,
    openLocationContentEditionModal,
    openCraneSelectionModal,
    openMoveModal,
    openClearLocationModal,
    openInfoLocationModal,
    safeCloseModal,
  } = useModalManagement({
    onClearSelections: clearAllSelections,
    onClearLocationEdition: clearLocationEdition,
    onClearLocationForClear: clearLocationForClear,
  });

  // Set the modal handlers in the refs for the authentication hook to use
  modalCloseRef.current = safeCloseModal;
  openCraneSelectionModalRef.current = openCraneSelectionModal;
  openLocationPickerModalRef.current = openLocationPickerModal;

  // Pause/Resume fetchData based on modal state
  useEffect(() => {
    if (currentModal !== MODAL_TYPES.NONE) dataManager.pauseFetchData();
    else dataManager.resumeFetchData();
  }, [currentModal]);

  // Location interaction handlers that use modal functions
  const handleLocationClick = (locationId: number) => {
    baseHandleLocationClick(locationId, openMoveModal);
  };

  const handleLocationPick = (locationId: number) => {
    baseHandleLocationPick(
      locationId,
      setSelectedLocationIdForEdition,
      openLocationContentEditionModal,
    );
  };

  const handleLocationHoldTap = (locationId: number) => {
    // Supervisors: Hold tap opens Edit Location Modal
    // Operators: Hold tap opens Clear Location Modal (existing behavior)
    if (authFlow.isSupervisor) {
      // Supervisor flow: Open Edit Location Modal directly
      setSelectedLocationIdForEdition(locationId);
      openLocationContentEditionModal();
    } else {
      // Operator flow: Open Clear Location Modal (existing behavior)
      baseHandleLocationHoldTap(locationId, openClearLocationModal);
    }
  };

  // Handle Clear button click from NavigationControls
  const handleClearButtonClick = () => {
    // Set the selectedLocationIdForClear from sourceLocationId before opening modal
    if (sourceLocationId) {
      const location = [
        ...dataManager.regularLocations,
        ...dataManager.orderedSpecialLocations,
      ].find((loc) => loc.LocationID === sourceLocationId);

      if (location?.ClearEnabled) {
        // Manually set selectedLocationIdForClear using the hook's setter
        // This ensures the modal has the correct location ID
        baseHandleLocationHoldTap(sourceLocationId, openClearLocationModal);
      }
    }
  };

  // Handle crane selection modal close with cleanup
  const handleCraneSelectionClose = () => {
    // Close the crane selection modal
    safeCloseModal();

    // Clear any pending Move/Clear modals and their selections
    // This prevents modals from appearing after successful login
    clearAllSelections();
    clearLocationForClear();
  };

  // Modal event handlers using the new hook
  const handleSearchClick = () => {
    openSearchModal();
  };

  // Handle clear search by combining both data hook and local state
  const handleClearSearch = () => {
    // Clear data hook search state
    dataManager.handleClearSearch();

    // Clear local search criteria state
    setSearchCriteria({
      locations: [],
      heats: [],
      sections: [],
      lengths: [],
      grades: [],
      qualities: [],
      destinations: [],
    });

    // Clear search notification
    setSearchNotificationMessage(null);
  };

  // Handle search criteria updates from modal
  const handleSearchCriteriaUpdate = (newCriteria: typeof searchCriteria) => {
    setSearchCriteria(newCriteria);
  };

  // Handle heat search from NavigationControls
  const handleHeatSearch = (heatName: string) => {
    // Set new search criteria with only heat name (non-numeric string)
    // The useSearchIntegration hook will automatically detect it's a heat name
    // and search by HeatNumber instead of HeatID
    const newCriteria = {
      locations: [],
      heats: [heatName],
      sections: [],
      lengths: [],
      grades: [],
      qualities: [],
      destinations: [],
    };

    setSearchCriteria(newCriteria);

    // The search will be triggered automatically by the searchCriteria useEffect in useSearchIntegration
  };

  // Monitor search results and show notification
  useEffect(() => {
    // Only show notification when search is active
    if (dataManager.isSearchActive && searchCriteria.heats.length > 0) {
      const heatName = searchCriteria.heats[0];

      if (dataManager.matchingPages.length === 0) {
        // No matches found - show warning
        setSearchNotificationMessage(`No matches found for heat "${heatName}"`);
        setSearchNotificationSeverity("warning");
      } else {
        // Matches found - navigate to first matching page
        const firstMatchingPage = dataManager.matchingPages[0];

        // Navigate to the first matching page
        handleRegularLocationPageChange(
          firstMatchingPage - regularLocationPage,
        );

        // Show success notification with page numbers
        const pagesText = dataManager.matchingPages.join(", ");
        const navigationText =
          dataManager.matchingPages.length > 1
            ? ` (navigated to page ${firstMatchingPage})`
            : "";
        setSearchNotificationMessage(
          `Heat ${heatName} found in page${
            dataManager.matchingPages.length > 1 ? "s" : ""
          } ${pagesText}${navigationText}`,
        );
        setSearchNotificationSeverity("success");
      }
    }
  }, [
    dataManager.isSearchActive,
    dataManager.matchingPages,
    searchCriteria.heats,
    regularLocationPage,
    handleRegularLocationPageChange,
  ]);

  return (
    <>
      <HelmetDexteel title="Billet Yard Inventory" />
      <LoadingAndEmptyStates
        isLoading={dataManager.isLoading}
        isRefreshing={dataManager.isRefreshing}
        hasData={dataManager.hasData}
      >
        <>
          {authFlow.isTouchMode && (
            <TopMenu
              selectedCraneId={authFlow.selectedCraneId}
              selectedOperatorId={authFlow.selectedOperatorId}
            />
          )}
          <Box
            sx={{
              padding: "20px",
              height: "90vh",
              overflow: "hidden",
              boxSizing: "border-box",
              position: "relative",
            }}
          >
            {/* Top Half: Regular Locations */}
            <RegularLocationsSection
              regularLocations={dataManager.regularLocations}
              groupsByLocationId={dataManager.groupsByLocationId}
              groupTotalsByLocationId={dataManager.groupTotalsByLocationId}
              locationTotalsById={dataManager.locationTotalsById}
              sourceLocationId={sourceLocationId}
              destinationLocationId={destinationLocationId}
              isLocationSearchHighlighted={
                dataManager.isLocationSearchHighlighted
              }
              getSearchMatchingGroupIds={dataManager.getSearchMatchingGroupIds}
              onLocationClick={handleLocationClick}
              onLocationHoldTap={handleLocationHoldTap}
              isSupervisor={authFlow.isSupervisor}
              isLoading={dataManager.isLoading}
              isRefreshing={dataManager.isRefreshing}
              hasData={dataManager.hasData}
            />

            {/* Bottom Half: Special Locations + Navigation */}
            <Box
              sx={{
                height: "calc(40vh)",
                display: "flex",
                gap: "8px",
              }}
            >
              {/* Special Locations */}
              <SpecialLocationsSection
                orderedSpecialLocations={dataManager.orderedSpecialLocations}
                groupsByLocationId={dataManager.groupsByLocationId}
                groupTotalsByLocationId={dataManager.groupTotalsByLocationId}
                locationTotalsById={dataManager.locationTotalsById}
                sourceLocationId={sourceLocationId}
                destinationLocationId={destinationLocationId}
                isLocationSearchHighlighted={
                  dataManager.isLocationSearchHighlighted
                }
                getSearchMatchingGroupIds={
                  dataManager.getSearchMatchingGroupIds
                }
                onLocationClick={handleLocationClick}
                onLocationHoldTap={handleLocationHoldTap}
                isSupervisor={authFlow.isSupervisor}
                isLoading={dataManager.isLoading}
                isRefreshing={dataManager.isRefreshing}
                hasData={dataManager.hasData}
              />

              {/* Navigation Controls */}
              <Paper
                elevation={2}
                sx={{
                  width: "320px",
                  height: "90%",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "space-between",
                  alignItems: "center",
                  flexShrink: 0,
                  padding: "20px 16px",
                  overflowY: "auto",
                  minHeight: 0,
                }}
              >
                <NavigationControls
                  regularLocationPage={regularLocationPage}
                  totalPages={TOTAL_PAGES}
                  onPageChange={handleRegularLocationPageChange}
                  onLocationEditionClick={authFlow.handleLocationEditionClick}
                  canAccessLocationContentEdition={
                    authFlow.canAccessLocationContentEdition
                  }
                  authentication={useAuthenticationControls({
                    selectedCraneId: authFlow.selectedCraneId,
                    selectedOperatorId: authFlow.selectedOperatorId,
                    isLoggedIn: authFlow.isLoggedIn,
                    canManageCranes: authFlow.canManageCranes,
                    showLogoutButton: authFlow.showLogoutButton,
                    isTouchMode: authFlow.isTouchMode,
                    onCraneSelectionClick: authFlow.handleCraneSelectionClick,
                    onLogoutClick: authFlow.handleLogoutClick,
                    onLoginClick: authFlow.isTouchMode
                      ? authFlow.handleLoginButtonClick
                      : undefined,
                  })}
                  search={useSearchControls({
                    isSearchActive: dataManager.isSearchActive,
                    onSearchClick: handleSearchClick,
                    onClearSearch: handleClearSearch,
                  })}
                  selectedLocationIdForClear={
                    // Show Clear button only if:
                    // 1. A source location is selected (first click in move billets flow)
                    // 2. AND that location has ClearEnabled = 1
                    // 3. AND a crane is selected (logged in)
                    sourceLocationId &&
                    authFlow.selectedCraneId &&
                    [
                      ...dataManager.regularLocations,
                      ...dataManager.orderedSpecialLocations,
                    ].find((loc) => loc.LocationID === sourceLocationId)
                      ?.ClearEnabled
                      ? sourceLocationId
                      : null
                  }
                  onClearLocationClick={handleClearButtonClick}
                  selectedLocationId={sourceLocationId}
                  onInfoClick={openInfoLocationModal}
                  isSupervisor={authFlow.isSupervisor}
                  onHeatSearch={handleHeatSearch}
                />
              </Paper>
            </Box>
          </Box>

          {/* Modals - Multiple modals can be open simultaneously for stacking */}
          <MoveBilletsModalWrapper
            currentModal={currentModal}
            isModalOpen={isModalOpen}
            sourceLocationId={sourceLocationId}
            destinationLocationId={destinationLocationId}
            storedCraneId={authFlow.selectedCraneId}
            onClose={safeCloseModal}
            refreshInterval={refreshInterval}
          />

          <EditLocationModalWrapper
            currentModal={currentModal}
            isModalOpen={isModalOpen}
            locationId={selectedLocationIdForEdition}
            storedCraneId={authFlow.selectedCraneId}
            onClose={safeCloseModal}
            refreshInterval={refreshInterval}
          />

          <ClearLocationModalWrapper
            currentModal={currentModal}
            isModalOpen={isModalOpen}
            locationId={
              // Use selectedLocationIdForClear (from hold tap) if set,
              // otherwise use sourceLocationId (from button click)
              selectedLocationIdForClear || sourceLocationId
            }
            storedCraneId={authFlow.selectedCraneId || 0}
            onClose={safeCloseModal}
            refreshInterval={refreshInterval}
          />

          <InfoLocationModalWrapper
            currentModal={currentModal}
            isModalOpen={isModalOpen}
            locationId={sourceLocationId}
            onClose={safeCloseModal}
            refreshInterval={refreshInterval}
          />

          {/* LoginModal handles both TouchKeyboardLogin and CraneSelectionModal */}
          <LoginModalWrapper
            isLoggedIn={authFlow.isLoggedIn}
            shouldBypassCraneSelection={authFlow.shouldBypassCraneSelection}
            selectedCraneId={authFlow.selectedCraneId}
            selectedOperatorId={authFlow.selectedOperatorId}
            isTouchMode={authFlow.isTouchMode}
            showTouchKeypad={authFlow.showTouchKeypad}
            canManageCranes={authFlow.canManageCranes}
            isModalOpen={isModalOpen}
            onOpenCraneSelection={authFlow.handleCraneSelectionClick}
            onTouchLoginSuccess={authFlow.handleTouchLoginSuccess}
            onTouchKeypadClose={authFlow.handleTouchKeypadClose}
            onCraneSelect={authFlow.handleCraneSelect}
            onOperatorSelect={authFlow.handleOperatorSelect}
            onCloseCraneSelection={handleCraneSelectionClose}
          />

          <SearchModalWrapper
            currentModal={currentModal}
            isModalOpen={isModalOpen}
            searchCriteria={searchCriteria}
            onClose={safeCloseModal}
            onSearchResults={dataManager.handleSearchResults}
            onCriteriaUpdate={handleSearchCriteriaUpdate}
          />

          <LocationSelectionModalWrapper
            currentModal={currentModal}
            isModalOpen={isModalOpen}
            locations={dataManager.allLocations}
            orderedSpecialLocations={dataManager.orderedSpecialLocations}
            groupsByLocationId={dataManager.groupsByLocationId}
            onClose={safeCloseModal}
            onLocationSelect={handleLocationPick}
          />

          <ErrorModal
            error={dataManager.error}
            onHide={() => dataManager.setError("")}
          />

          {/* Permission notification */}
          <NotificationSnackBar
            message={
              authFlow.permissionAlert.open
                ? authFlow.permissionAlert.message
                : null
            }
            onHide={authFlow.handleClosePermissionAlert}
            severity="warning"
          />

          {/* Heat search notification */}
          <NotificationSnackBar
            message={searchNotificationMessage}
            onHide={() => setSearchNotificationMessage(null)}
            severity={searchNotificationSeverity}
          />
        </>
      </LoadingAndEmptyStates>
    </>
  );
};
